import { CSSProperties, FocusEventHandler } from "react";
import ReactSelect, {
    ActionMeta,
    DropdownIndicatorProps,
    GroupBase,
    InputProps,
    OptionProps,
    SingleValue,
    ValueContainerProps,
    components
} from "react-select";

import { ClassesEnum } from "@/ts/enums/ClassesEnum";
import { useTheme } from "@emotion/react";
import { faAngleDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { SelectComponents } from "node_modules/react-select/dist/declarations/src/components";

import { Box } from "@chakra-ui/react";

import { SelectOptionInterface } from "@ts/interfaces/UI/UiInterface";

import { getTrackingId } from "@utils/utils.ts";

// Define the shape of an option
type OptionType = {
    value: string;
    label: string;
};

interface CustomStyles {
    control?: CSSProperties;
    valueContainer?: CSSProperties;
    placeholder?: CSSProperties;
    menu?: CSSProperties;
    menuList?: CSSProperties;
    option?: CSSProperties;
    indicatorSeparator?: CSSProperties;
    noOptionsMessage?: CSSProperties;
}

interface PropTypes {
    options: SelectOptionInterface[];
    defaultValue?: { label: string; value: string };
    placeholder: React.ReactNode;
    value: SelectOptionInterface | null;
    dataTrackingId?: string;
    option?: (
        props: OptionProps<
            SelectOptionInterface,
            false,
            GroupBase<SelectOptionInterface>
        >
    ) => JSX.Element;
    valueContainer?: ({
        children,
        ...props
    }: ValueContainerProps<
        SelectOptionInterface,
        false,
        GroupBase<SelectOptionInterface>
    >) => JSX.Element;
    onChange: (
        v: SingleValue<SelectOptionInterface>,
        a: ActionMeta<SelectOptionInterface>
    ) => void;
    onBlur?: FocusEventHandler<HTMLInputElement>;
    styles?: CustomStyles;
    isInvalid?: boolean;
    isDisabled?: boolean;
    bgColor?: string;
}

const DropdownIndicator = (
    props: DropdownIndicatorProps<
        SelectOptionInterface,
        false,
        GroupBase<SelectOptionInterface>
    >
) => {
    return (
        <components.DropdownIndicator {...props}>
            <Box px={2}>
                <FontAwesomeIcon
                    icon={faAngleDown}
                    color="var(--euka-colors-gray-900)"
                    fontSize={15}
                />
            </Box>
        </components.DropdownIndicator>
    );
};

const DefaultInput: React.FC<InputProps<OptionType>> = props => {
    return (
        <components.Input
            {...props}
            data-tracking-id={getTrackingId(
                [ClassesEnum.Select],
                "selectOption"
            )}
        />
    );
};

const DefaultOption = (
    props: OptionProps<
        SelectOptionInterface,
        false,
        GroupBase<SelectOptionInterface>
    >
) => {
    const { data } = props;
    return (
        <components.Option {...props}>
            <div
                data-tracking-id={getTrackingId(
                    [ClassesEnum.Select, ClassesEnum.Option],
                    data.label
                )}
            >
                {data.label}
            </div>
        </components.Option>
    );
};

const Select = ({
    options = [],
    placeholder,
    defaultValue,
    value,
    option: Option,
    valueContainer: ValueContainer,
    styles = {},
    isInvalid,
    isDisabled,
    dataTrackingId,
    bgColor,
    onChange,
    onBlur
}: PropTypes) => {
    const theme = useTheme();

    const customComponents:
        | Partial<
              SelectComponents<
                  SelectOptionInterface,
                  false,
                  GroupBase<SelectOptionInterface>
              >
          >
        | undefined = {};

    customComponents.DropdownIndicator = !isDisabled ? DropdownIndicator : null;

    if (Option) customComponents.Option = Option;
    if (!Option) customComponents.Option = DefaultOption;
    if (ValueContainer) customComponents.ValueContainer = ValueContainer;
    customComponents.Input = DefaultInput;

    return (
        <ReactSelect
            menuPosition="fixed"
            isDisabled={isDisabled}
            defaultValue={defaultValue}
            value={value}
            components={customComponents}
            onChange={onChange}
            onBlur={onBlur}
            placeholder={placeholder}
            options={options}
            data-tracking-id={dataTrackingId}
            styles={{
                control: (baseStyles, { menuIsOpen, isDisabled }) => {

                    const bg = bgColor ? bgColor : "white";

                    return {
                        ...baseStyles,
                        "alignItems": "center",
                        "boxShadow": menuIsOpen
                            ? "0 0 0 1px var(--euka-colors-burntYellow-200)"
                            : "0px 0px 1px 0px rgba(50, 50, 71, 0.20), 0px 1px 2px 0px rgba(50, 50, 71, 0.08)",
                        [`@media screen and (max-width: ${(
                            theme as { breakpoints: { sm: string } }
                        ).breakpoints?.sm})`]: {
                            borderWidth: 1
                        },
                        "borderWidth": 1,
                        "borderColor": isInvalid
                            ? "var(--euka-colors-danger-500)"
                            : menuIsOpen
                            ? "var(--euka-colors-burntYellow-300)"
                            : "var(--euka-colors-gray-300)",
                        "minHeight": "var(--euka-sizes-12)",
                        "borderRadius": 12,
                        "&:hover": {
                            borderColor: isInvalid
                                ? "var(--euka-colors-danger-500)"
                                : menuIsOpen
                                ? "var(--euka-colors-burntYellow-300)"
                                : "var(--euka-colors-gray-300)"
                        },
                        "background": isDisabled
                            ? "var(--euka-colors-gray-300)"
                            : bg,
                        ...styles.control
                    };
                },
                valueContainer: baseStyle => {
                    return {
                        ...baseStyle,
                        padding: "0 16px",
                        fontSize: 15,
                        ...styles.valueContainer
                    };
                },
                placeholder: baseStyles => {
                    return {
                        ...baseStyles,
                        color: "var(--euka-colors-gray-500)",
                        fontWeight: "bold",
                        ...styles.placeholder
                    };
                },
                menu: baseStyle => {
                    return {
                        ...baseStyle,
                        borderRadius: 12,
                        boxShadow:
                            "0px 10px 16px 0px rgba(20, 37, 63, 0.06), 0px 0px 1px 0px rgba(12, 26, 75, 0.10)",
                        border: "1px solid var(--euka-colors-gray-300)",
                        ...styles.menu
                    };
                },
                menuList: baseStyle => {
                    return {
                        ...baseStyle,
                        padding: 13,
                        display: "flex",
                        flexDirection: "column",
                        gap: 5,
                        ...styles.menuList
                    };
                },
                option: (baseStyle, { isSelected }) => {
                    return {
                        ...baseStyle,
                        "fontSize": 15,
                        "cursor": "pointer",
                        "borderRadius": 12,
                        "backgroundColor": isSelected
                            ? "var(--euka-colors-gray-200)"
                            : "transparent",
                        "color": "var(--euka-colors-gray-800)",
                        "&:active": {
                            backgroundColor: "var(--euka-colors-gray-300)"
                        },
                        ...styles.option
                    };
                },
                indicatorSeparator: baseStyle => {
                    return {
                        ...baseStyle,
                        display: "none",
                        ...styles.indicatorSeparator
                    };
                },
                noOptionsMessage: baseStyle => {
                    return {
                        ...baseStyle,
                        color: "var(--euka-colors-gray-500)",
                        ...styles.noOptionsMessage
                    };
                }
            }}
        />
    );
};

export default Select;
